import {verifiedFetch} from '@github-ui/verified-fetch'
import {useCallback, useState} from 'react'

interface UseApproveWorkflowsResult {
  isPending: boolean
  error: string | null
  approve: () => Promise<void>
}

/**
 * Hook to approve pending workflow runs for a pull request.
 * Uses the existing REST endpoint: POST /{owner}/{repo}/pull/{number}/run_workflows
 */
export function useApproveWorkflows(
  repositoryNwo: string,
  pullRequestNumber: number,
  onSuccess?: () => void,
): UseApproveWorkflowsResult {
  const [isPending, setIsPending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const approve = useCallback(async () => {
    setIsPending(true)
    setError(null)

    try {
      const url = `/${repositoryNwo}/pull/${pullRequestNumber}/run_workflows`
      const response = await verifiedFetch(url, {method: 'POST'})

      if (!response.ok) {
        const message = response.statusText || 'Failed to approve workflows'
        throw new Error(message)
      }

      onSuccess?.()
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to approve workflows'
      setError(message)
    } finally {
      setIsPending(false)
    }
  }, [repositoryNwo, pullRequestNumber, onSuccess])

  return {isPending, error, approve}
}
